import React, { FC } from 'react';
import { Caption, LabelBold } from '../typography/typography';
import { BadgeContainer } from '../badges/badges';
import {
	BasicHorizontalCardWrapper,
	TextWrapper,
	ImageWrapper,
	BasicHorizontalCardDetailsWrapper,
	ButtonWrapper,
	ActionContentWrapper,
	BadgeWrapper,
	ImageTextWrapper
} from './base/basic-horizontal-base';

const SIZES = '180px'; // Smallest we've got as of now. Container is actually only 72x72, but we will prevent larger images to load like 500x500x etc.

interface Props {
	imageSrc: string;
	imageSrcSet?: string;
	imageComponent?: React.ReactNode;
	imageText?: React.ReactNode;
	topText?: string;
	topTextColor?: string;
	middleText?: string;
	middleTextColor?: string;
	bottomText?: string;
	bottomTextColor?: string;
	hasPrimaryButton?: boolean;
	buttonBgColor?: string;
	onClick?: () => void;
	badge?: React.ReactNode;
	dataAt?: string;
	dataTag?: string;
	disabled?: boolean;
	actionContent?: React.ReactNode;
	actionContentPosition?: 'start' | 'center' | 'end';
	topTextTruncate?: boolean;
	middleTextTruncate?: boolean;
	bottomTextTruncate?: boolean;
	iconRight: React.ReactNode;
	height?: string;
	sizes?: string;
}

/**
 *
 * @param {String} imageSrc
 * @param {String} imageSrcSet
 * @param {React.Node} imageText
 * @param {React.Node} imageComponent
 * @param {String} topText
 * @param {String} topTextColor
 * @param {String} middleText
 * @param {String} middleTextColor
 * @param {String} bottomText
 * @param {String} bottomTextColor
 * @param {Boolean} hasPrimaryButton
 * @param {String} buttonBgColor
 * @param onClick
 * @param {React.Node} badge
 * @param {String} dataAt
 * @param {String} dataTag
 * @param {Boolean} disabled
 * @param {React.Node} actionContent
 * @param {String} actionContentPosition
 * @param {Boolean} topTextTruncate
 * @param {Boolean} middleTextTruncate
 * @param {Boolean} bottomTextTruncate
 * @param {React.Node} iconRight
 * @param {String} height
 * @param sizes
 */
const BasicHorizontalCard: FC<Props> = ({
	imageSrc,
	imageSrcSet,
	imageText,
	imageComponent,
	topText,
	topTextColor,
	middleText,
	middleTextColor,
	bottomText,
	bottomTextColor,
	hasPrimaryButton,
	buttonBgColor,
	onClick,
	badge,
	dataAt,
	dataTag,
	disabled,
	actionContent,
	actionContentPosition,
	topTextTruncate,
	middleTextTruncate,
	bottomTextTruncate,
	iconRight,
	height,
	sizes
}) => (
	<BasicHorizontalCardWrapper
		data-at={dataAt}
		onClick={disabled ? undefined : onClick}
		hasOnClick={onClick}
		disabled={disabled}
		data-tag={dataTag}
		role="presentation"
		className="basic-horizontal-card-wrapper"
	>
		<ImageWrapper height={height}>
			{imageComponent && imageComponent}
			{imageSrc && <img src={imageSrc} srcSet={imageSrcSet} alt="" sizes={sizes || SIZES} />}
			{imageText && <ImageTextWrapper>{imageText}</ImageTextWrapper>}
		</ImageWrapper>
		<BasicHorizontalCardDetailsWrapper height={height} withPadding={!!actionContent}>
			{topText && (
				<TextWrapper
					customColor={topTextColor}
					disabled={disabled}
					truncate={topTextTruncate}
					className="basic-horizontal-card--top-text"
				>
					<LabelBold>{topText}</LabelBold>
				</TextWrapper>
			)}
			{middleText && (
				<TextWrapper
					customColor={middleTextColor}
					disabled={disabled}
					truncate={middleTextTruncate}
					className="basic-horizontal-card--middle-text"
				>
					<Caption>{middleText}</Caption>
				</TextWrapper>
			)}
			{bottomText && (
				<TextWrapper
					customColor={bottomTextColor}
					truncate={bottomTextTruncate}
					className="basic-horizontal-card--bottom-text"
				>
					<Caption>{bottomText}</Caption>
				</TextWrapper>
			)}
		</BasicHorizontalCardDetailsWrapper>
		{badge && (
			<BadgeWrapper>
				<BadgeContainer className="basic-horizontal-card--bottom-tag">{badge}</BadgeContainer>
			</BadgeWrapper>
		)}
		{hasPrimaryButton && (
			<ButtonWrapper buttonBgColor={buttonBgColor} disabled={disabled} className="basic-horizontal-card-icon">
				{iconRight}
			</ButtonWrapper>
		)}
		{actionContent && (
			<ActionContentWrapper disabled={disabled} position={actionContentPosition}>
				{actionContent}
			</ActionContentWrapper>
		)}
	</BasicHorizontalCardWrapper>
);

export default BasicHorizontalCard;
